/**
 * Cabinet-run drawing overlay.
 *
 * Canvas drawing for the cabinet-run-in-progress preview. Unlike the room
 * polygon, a cabinet run is an OPEN polyline along walls (no fill, no close).
 * The solid line follows `pathPoints` — the clicked points plus the inferred
 * corner at each wall transition — so an L reads as an L, not a diagonal.
 * Operates on a 2D canvas context already in world-space transform.
 */

import { drawSnapIndicator } from './roomDrawingUtils';

const RUN_COLOR = '#B45309'; // amber-800 — distinct from room-drawing blue
const PREVIEW_COLOR = '#F59E0B'; // amber-500
const VERTEX_RADIUS = 0.08; // meters

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{x:number,z:number}>} pathPoints  run path incl. inferred corners
 * @param {Array<{wallId:string, point:{x:number,z:number}}>} vertices  clicked points
 * @param {{x:number,z:number}|null} cursorPos
 * @param {{type:string, position:{x:number,z:number}}|null} snapInfo
 * @param {number} scale
 */
export function drawCabinetRunPreview(
  ctx,
  pathPoints,
  vertices,
  cursorPos,
  snapInfo,
  scale
) {
  const path = pathPoints || [];
  const dots = (vertices || []).map((v) => v.point);
  const lineWidth = 2 / scale;
  const dashSize = 0.1;

  ctx.save();

  // Placed path — follows the inferred corners (solid).
  if (path.length >= 2) {
    ctx.strokeStyle = RUN_COLOR;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(path[0].x, path[0].z);
    for (let i = 1; i < path.length; i++) ctx.lineTo(path[i].x, path[i].z);
    ctx.stroke();
  }

  // Dashed preview from the last placed point to the cursor.
  if (cursorPos && path.length > 0) {
    const last = path[path.length - 1];
    ctx.strokeStyle = PREVIEW_COLOR;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([dashSize, dashSize]);
    ctx.beginPath();
    ctx.moveTo(last.x, last.z);
    ctx.lineTo(cursorPos.x, cursorPos.z);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Clicked vertices as dots.
  for (const p of dots) {
    ctx.beginPath();
    ctx.arc(p.x, p.z, VERTEX_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = RUN_COLOR;
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = lineWidth * 0.6;
    ctx.stroke();
  }

  ctx.restore();

  // Snap ring — reuse the room-drawing indicator.
  if (snapInfo && snapInfo.position) {
    drawSnapIndicator(
      ctx,
      snapInfo.position,
      snapInfo.type,
      scale,
      snapInfo.target
    );
  }
}
